/**
 * Funzioni di aggregazione dati per il Bilancio Competenza.
 * Estratte da BilancioCompetenzaPage per migliorare la manutenibilità.
 */

const EMPTY_MESI = { costi: {}, ricavi: {}, totali: {}, costiPerCategoria: {}, ricaviPerCategoria: {}, costiOriginaliPerCategoria: {}, totaliOriginali: {}, ripartizioneInfo: {}, dettaglioMap: {}, dettaglioMapOriginali: {} };
const EMPTY_CDC = { costi: {}, ricavi: {}, totali: {}, costiPerCategoria: {}, ricaviPerCategoria: {}, costiOriginaliPerCategoria: {}, totaliOriginali: {}, dettaglioMap: {}, dettaglioMapOriginali: {} };

/**
 * Aggrega dati per visualizzazione tabellare - Modalità MESI come colonne.
 * @param {Object} reportData - Dati raw dal backend, indicizzati per mese
 * @param {string[]} selectedMesi - Array di mesi selezionati (es. ["01","02",...])
 * @returns {Object} Dati aggregati con costi, ricavi, totali, categorie, ripartizioneInfo
 */
export function aggregateByMesi(reportData, selectedMesi) {
  if (!reportData) return EMPTY_MESI;

  const costi = {};
  const ricavi = {};
  const costiPerCategoria = {};
  const ricaviPerCategoria = {};
  const costiOriginaliPerCategoria = {};
  const totali = { costi: {}, ricavi: {}, risultato: {} };
  const totaliOriginali = { costi: {}, ricavi: {}, risultato: {} };
  const ripartizioneInfo = {};
  const dettaglioMap = {}; // dettaglioMap[contoNome][columnKey] = [...entries]

  for (const mese of selectedMesi) {
    const meseData = reportData[mese];
    if (!meseData) continue;

    let totaleCosti = 0;
    let totaleRicavi = 0;
    let totaleCostiOrig = 0;

    for (const cdcKey of Object.keys(meseData)) {
      const data = meseData[cdcKey];
      if (!data) continue;

      totaleCosti += data.totale_costi || 0;
      totaleRicavi += data.totale_ricavi || 0;

      // Costi post-ripartizione per conto e categoria
      if (data.costi?.per_conto) {
        for (const conto of data.costi.per_conto) {
          const key = conto.conto_nome || "Senza conto";
          const catId = conto.categoria_id || '__senza_categoria__';
          const catNome = conto.categoria_nome || 'Senza categoria';
          const catCodice = conto.categoria_codice || '';
          const catOrdine = conto.categoria_ordine || 999;

          if (!costi[key]) costi[key] = { nome: key, codice: conto.conto_codice, categoria_id: catId, categoria_nome: catNome, values: {} };
          costi[key].values[mese] = (costi[key].values[mese] || 0) + conto.totale;

          if (!costiPerCategoria[catId]) {
            costiPerCategoria[catId] = { nome: catNome, codice: catCodice, ordine: catOrdine, conti: {}, totali: {} };
          }
          if (!costiPerCategoria[catId].conti[key]) {
            costiPerCategoria[catId].conti[key] = { nome: key, codice: conto.conto_codice, categoria_id: catId, categoria_codice: catCodice, values: {} };
          }
          costiPerCategoria[catId].conti[key].values[mese] = (costiPerCategoria[catId].conti[key].values[mese] || 0) + conto.totale;
          costiPerCategoria[catId].totali[mese] = (costiPerCategoria[catId].totali[mese] || 0) + conto.totale;

          // Colleziona dettaglio per drill-down
          if (conto.dettaglio && conto.dettaglio.length > 0) {
            if (!dettaglioMap[key]) dettaglioMap[key] = {};
            if (!dettaglioMap[key][mese]) dettaglioMap[key][mese] = [];
            dettaglioMap[key][mese].push(...conto.dettaglio);
          }

          // Breakdown diretto/ripartizione
          if (conto.quota_diretta !== undefined || conto.quota_ripartizione !== undefined) {
            if (!ripartizioneInfo[key]) ripartizioneInfo[key] = {};
            if (!ripartizioneInfo[key][mese]) ripartizioneInfo[key][mese] = { diretta: 0, ripartizione: 0, dettaglio: [] };
            ripartizioneInfo[key][mese].diretta += conto.quota_diretta || 0;
            ripartizioneInfo[key][mese].ripartizione += conto.quota_ripartizione || 0;
            if (conto.dettaglio_ripartizione) {
              ripartizioneInfo[key][mese].dettaglio.push(...conto.dettaglio_ripartizione);
            }
          }
        }
      }

      // Costi originali (pre-ripartizione)
      if (data.costi_originali?.per_conto) {
        totaleCostiOrig += data.costi_originali.per_conto.reduce((s, c) => s + (c.totale || 0), 0);
        for (const conto of data.costi_originali.per_conto) {
          const key = conto.conto_nome || "Senza conto";
          const catId = conto.categoria_id || '__senza_categoria__';
          const catNome = conto.categoria_nome || 'Senza categoria';
          const catCodice = conto.categoria_codice || '';
          const catOrdine = conto.categoria_ordine || 999;

          if (!costiOriginaliPerCategoria[catId]) {
            costiOriginaliPerCategoria[catId] = { nome: catNome, codice: catCodice, ordine: catOrdine, conti: {}, totali: {} };
          }
          if (!costiOriginaliPerCategoria[catId].conti[key]) {
            costiOriginaliPerCategoria[catId].conti[key] = { nome: key, codice: conto.conto_codice, categoria_id: catId, categoria_codice: catCodice, values: {} };
          }
          costiOriginaliPerCategoria[catId].conti[key].values[mese] = (costiOriginaliPerCategoria[catId].conti[key].values[mese] || 0) + conto.totale;
          costiOriginaliPerCategoria[catId].totali[mese] = (costiOriginaliPerCategoria[catId].totali[mese] || 0) + conto.totale;
        }
      }

      // Ricavi per conto e categoria
      if (data.ricavi?.per_conto) {
        for (const conto of data.ricavi.per_conto) {
          const key = conto.conto_nome || "Senza conto";
          const catId = conto.categoria_id || '__senza_categoria__';
          const catNome = conto.categoria_nome || 'Senza categoria';
          const catCodice = conto.categoria_codice || '';
          const catOrdine = conto.categoria_ordine || 999;

          if (!ricavi[key]) ricavi[key] = { nome: key, codice: conto.conto_codice, categoria_id: catId, categoria_nome: catNome, values: {} };
          ricavi[key].values[mese] = (ricavi[key].values[mese] || 0) + conto.totale;

          if (!ricaviPerCategoria[catId]) {
            ricaviPerCategoria[catId] = { nome: catNome, codice: catCodice, ordine: catOrdine, conti: {}, totali: {} };
          }
          if (!ricaviPerCategoria[catId].conti[key]) {
            ricaviPerCategoria[catId].conti[key] = { nome: key, codice: conto.conto_codice, categoria_id: catId, categoria_codice: catCodice, values: {} };
          }
          ricaviPerCategoria[catId].conti[key].values[mese] = (ricaviPerCategoria[catId].conti[key].values[mese] || 0) + conto.totale;
          ricaviPerCategoria[catId].totali[mese] = (ricaviPerCategoria[catId].totali[mese] || 0) + conto.totale;

          // Colleziona dettaglio ricavi per drill-down
          if (conto.dettaglio && conto.dettaglio.length > 0) {
            if (!dettaglioMap[key]) dettaglioMap[key] = {};
            if (!dettaglioMap[key][mese]) dettaglioMap[key][mese] = [];
            dettaglioMap[key][mese].push(...conto.dettaglio);
          }
        }
      }
    }

    totali.costi[mese] = totaleCosti;
    totali.ricavi[mese] = totaleRicavi;
    totali.risultato[mese] = totaleRicavi - totaleCosti;
    totaliOriginali.costi[mese] = totaleCostiOrig || totaleCosti;
    totaliOriginali.ricavi[mese] = totaleRicavi;
    totaliOriginali.risultato[mese] = totaleRicavi - (totaleCostiOrig || totaleCosti);
  }

  return { costi, ricavi, totali, costiPerCategoria, ricaviPerCategoria, costiOriginaliPerCategoria, totaliOriginali, ripartizioneInfo, dettaglioMap, dettaglioMapOriginali: {} };
}

/**
 * Aggrega dati per visualizzazione tabellare - Modalità CDC come colonne.
 * @param {Object} reportData - Dati raw dal backend, indicizzati per CDC
 * @param {string[]} selectedMesi - Array di mesi selezionati
 * @param {string[]} selectedCentriCosto - Array di CDC ID selezionati
 * @returns {Object} Dati aggregati con costi, ricavi, totali, categorie, costiOriginaliPerCategoria, totaliOriginali
 */
export function aggregateByCDC(reportData, selectedMesi, selectedCentriCosto) {
  if (!reportData) return EMPTY_CDC;

  const costi = {};
  const ricavi = {};
  const costiPerCategoria = {};
  const ricaviPerCategoria = {};
  const costiOriginaliPerCategoria = {};
  const totali = { costi: {}, ricavi: {}, risultato: {} };
  const totaliOriginali = { costi: {}, ricavi: {}, risultato: {} };
  const dettaglioMap = {};
  const dettaglioMapOriginali = {};

  const cdcIds = [...selectedCentriCosto];
  if (reportData["__senza_cdc__"]) {
    cdcIds.push("__senza_cdc__");
  }

  for (const cdcId of cdcIds) {
    const cdcData = reportData[cdcId];
    if (!cdcData) continue;

    let totaleCosti = 0;
    let totaleRicavi = 0;
    let totaleCostiOrig = 0;

    for (const mese of selectedMesi) {
      const data = cdcData[mese];
      if (!data) continue;

      totaleCosti += data.totale_costi || 0;
      totaleRicavi += data.totale_ricavi || 0;
      totaleCostiOrig += data.costi_originali?.totale || 0;

      if (data.costi?.per_conto) {
        for (const conto of data.costi.per_conto) {
          const key = conto.conto_nome || "Senza conto";
          const catId = conto.categoria_id || '__senza_categoria__';
          const catNome = conto.categoria_nome || 'Senza categoria';
          const catCodice = conto.categoria_codice || '';
          const catOrdine = conto.categoria_ordine || 999;

          if (!costi[key]) costi[key] = { nome: key, codice: conto.conto_codice, categoria_id: catId, categoria_nome: catNome, values: {} };
          costi[key].values[cdcId] = (costi[key].values[cdcId] || 0) + conto.totale;

          if (!costiPerCategoria[catId]) {
            costiPerCategoria[catId] = { nome: catNome, codice: catCodice, ordine: catOrdine, conti: {}, totali: {} };
          }
          if (!costiPerCategoria[catId].conti[key]) {
            costiPerCategoria[catId].conti[key] = { nome: key, codice: conto.conto_codice, categoria_id: catId, categoria_codice: catCodice, values: {} };
          }
          costiPerCategoria[catId].conti[key].values[cdcId] = (costiPerCategoria[catId].conti[key].values[cdcId] || 0) + conto.totale;
          costiPerCategoria[catId].totali[cdcId] = (costiPerCategoria[catId].totali[cdcId] || 0) + conto.totale;

          if (conto.dettaglio && conto.dettaglio.length > 0) {
            if (!dettaglioMap[key]) dettaglioMap[key] = {};
            if (!dettaglioMap[key][cdcId]) dettaglioMap[key][cdcId] = [];
            dettaglioMap[key][cdcId].push(...conto.dettaglio);
          }
        }
      }

      // Costi originali (pre-ripartizione) per CDC
      if (data.costi_originali?.per_conto) {
        for (const conto of data.costi_originali.per_conto) {
          const key = conto.conto_nome || "Senza conto";
          const catId = conto.categoria_id || '__senza_categoria__';
          const catNome = conto.categoria_nome || 'Senza categoria';
          const catCodice = conto.categoria_codice || '';
          const catOrdine = conto.categoria_ordine || 999;

          if (!costiOriginaliPerCategoria[catId]) {
            costiOriginaliPerCategoria[catId] = { nome: catNome, codice: catCodice, ordine: catOrdine, conti: {}, totali: {} };
          }
          if (!costiOriginaliPerCategoria[catId].conti[key]) {
            costiOriginaliPerCategoria[catId].conti[key] = { nome: key, codice: conto.conto_codice, categoria_id: catId, categoria_codice: catCodice, values: {} };
          }
          costiOriginaliPerCategoria[catId].conti[key].values[cdcId] = (costiOriginaliPerCategoria[catId].conti[key].values[cdcId] || 0) + conto.totale;
          costiOriginaliPerCategoria[catId].totali[cdcId] = (costiOriginaliPerCategoria[catId].totali[cdcId] || 0) + conto.totale;

          if (conto.dettaglio && conto.dettaglio.length > 0) {
            if (!dettaglioMapOriginali[key]) dettaglioMapOriginali[key] = {};
            if (!dettaglioMapOriginali[key][cdcId]) dettaglioMapOriginali[key][cdcId] = [];
            dettaglioMapOriginali[key][cdcId].push(...conto.dettaglio);
          }
        }
      }

      if (data.ricavi?.per_conto) {
        for (const conto of data.ricavi.per_conto) {
          const key = conto.conto_nome || "Senza conto";
          const catId = conto.categoria_id || '__senza_categoria__';
          const catNome = conto.categoria_nome || 'Senza categoria';
          const catCodice = conto.categoria_codice || '';
          const catOrdine = conto.categoria_ordine || 999;

          if (!ricavi[key]) ricavi[key] = { nome: key, codice: conto.conto_codice, categoria_id: catId, categoria_nome: catNome, values: {} };
          ricavi[key].values[cdcId] = (ricavi[key].values[cdcId] || 0) + conto.totale;

          if (!ricaviPerCategoria[catId]) {
            ricaviPerCategoria[catId] = { nome: catNome, codice: catCodice, ordine: catOrdine, conti: {}, totali: {} };
          }
          if (!ricaviPerCategoria[catId].conti[key]) {
            ricaviPerCategoria[catId].conti[key] = { nome: key, codice: conto.conto_codice, categoria_id: catId, categoria_codice: catCodice, values: {} };
          }
          ricaviPerCategoria[catId].conti[key].values[cdcId] = (ricaviPerCategoria[catId].conti[key].values[cdcId] || 0) + conto.totale;
          ricaviPerCategoria[catId].totali[cdcId] = (ricaviPerCategoria[catId].totali[cdcId] || 0) + conto.totale;

          if (conto.dettaglio && conto.dettaglio.length > 0) {
            if (!dettaglioMap[key]) dettaglioMap[key] = {};
            if (!dettaglioMap[key][cdcId]) dettaglioMap[key][cdcId] = [];
            dettaglioMap[key][cdcId].push(...conto.dettaglio);
          }
        }
      }
    }

    totali.costi[cdcId] = totaleCosti;
    totali.ricavi[cdcId] = totaleRicavi;
    totali.risultato[cdcId] = totaleRicavi - totaleCosti;
    totaliOriginali.costi[cdcId] = totaleCostiOrig || totaleCosti;
    totaliOriginali.ricavi[cdcId] = totaleRicavi;
    totaliOriginali.risultato[cdcId] = totaleRicavi - (totaleCostiOrig || totaleCosti);
  }

  return { costi, ricavi, totali, costiPerCategoria, ricaviPerCategoria, costiOriginaliPerCategoria, totaliOriginali, dettaglioMap, dettaglioMapOriginali };
}

/**
 * Aggrega dati CDC riepilogo da tutte le risposte mensili.
 * @param {Object} reportData
 * @param {string[]} selectedMesi
 * @returns {Object|null}
 */
export function aggregateCdcRiepilogo(reportData, selectedMesi) {
  const cdcPerMese = {};
  const cdcOrigPerMese = {};
  const ripartDettaglio = [];
  const cdcRipartibili = [];

  for (const mese of selectedMesi) {
    const meseData = reportData[mese];
    if (!meseData) continue;

    const apiData = meseData?.["__all__"] || Object.values(meseData || {})[0];
    if (!apiData) continue;

    for (const cdc of (apiData.per_centro_costo || [])) {
      if (!cdcPerMese[cdc.centro_costo_id]) {
        cdcPerMese[cdc.centro_costo_id] = { id: cdc.centro_costo_id, nome: cdc.centro_costo_nome, mesi: {} };
      }
      cdcPerMese[cdc.centro_costo_id].mesi[mese] = (cdcPerMese[cdc.centro_costo_id].mesi[mese] || 0) + cdc.totale;
    }

    for (const cdc of (apiData.per_centro_costo_originale || [])) {
      if (!cdcOrigPerMese[cdc.centro_costo_id]) {
        cdcOrigPerMese[cdc.centro_costo_id] = { id: cdc.centro_costo_id, nome: cdc.centro_costo_nome, mesi: {} };
      }
      cdcOrigPerMese[cdc.centro_costo_id].mesi[mese] = (cdcOrigPerMese[cdc.centro_costo_id].mesi[mese] || 0) + cdc.totale;
    }

    for (const r of (apiData.ripartizioni_applicate || [])) {
      ripartDettaglio.push({ ...r, mese });
    }

    for (const c of (apiData.cdc_da_ripartire || [])) {
      if (!cdcRipartibili.find(x => x.id === c.id)) cdcRipartibili.push(c);
    }
  }

  return {
    ripartiti: Object.values(cdcPerMese),
    originali: Object.values(cdcOrigPerMese),
    ripartizioni: ripartDettaglio,
    cdcRipartibili
  };
}
